import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { X, Mail, Calendar, CreditCard, Award, Edit } from 'lucide-react';
import { toast } from 'sonner';
import EditUserModal from './EditUserModal';

const UserDetailModal = ({ user, onClose, onUpdate, isSuperAdmin }) => {
  const [dimensionProfile, setDimensionProfile] = useState(null);
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    if (currentUser.assessment_result) {
      fetchDimensionDetails();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const handleUserUpdate = (updatedUser) => {
    if (updatedUser === null) {
      // User was deleted
      onUpdate();
      onClose();
    } else {
      // User was updated
      setCurrentUser({ ...currentUser, user: updatedUser });
      if (onUpdate) {
        onUpdate();
      }
    }
  };

  const fetchDimensionDetails = async () => {
    try {
      const profileRes = await axios.get(`${API}/dimension/${currentUser.assessment_result.primary_dimension}`);
      setDimensionProfile(profileRes.data);

      const rolesRes = await axios.get(`${API}/roles/suggestions/${currentUser.assessment_result.primary_dimension}`);
      setRoleSuggestions(rolesRes.data.ministry_roles || rolesRes.data.roles || []);
    } catch (error) {
      toast.error('Failed to load dimension details');
    } finally {
      setLoading(false);
    }
  };

  const dimensionNames = {
    B: 'Brainstorm',
    U: 'Uncover',
    I: 'Inspect',
    L: 'Lift',
    D: 'Deliver',
    R: 'Rally'
  };

  const dimensionColors = {
    B: 'bg-purple-100 text-purple-700',
    U: 'bg-blue-100 text-blue-700',
    I: 'bg-green-100 text-green-700',
    L: 'bg-pink-100 text-pink-700',
    D: 'bg-orange-100 text-orange-700',
    R: 'bg-red-100 text-red-700'
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const maxScore = currentUser.assessment_result ? Math.max(...Object.values(currentUser.assessment_result.scores)) : 35;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          data-testid="close-user-detail"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-8">
          {/* User Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{currentUser.user.name}</h2>
                <div className="flex items-center gap-4 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>{currentUser.user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    <span>{currentUser.user.credits || 0} Credits</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="text-sm px-3 py-1">
                  {currentUser.user.role === 'admin' ? 'Administrator' : 'Volunteer'}
                </Badge>
                <Button
                  onClick={() => setShowEditModal(true)}
                  size="sm"
                  variant="outline"
                  className="ml-2"
                  data-testid="edit-user-btn"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </div>
            </div>

            {currentUser.user.created_at && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>Registered: {formatDate(currentUser.user.created_at)}</span>
              </div>
            )}
          </div>

          {!currentUser.assessment_result ? (
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-6">
                <p className="text-center text-orange-800 text-lg">
                  <Award className="w-6 h-6 inline-block mr-2" />
                  Assessment not yet completed
                </p>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="text-center py-12">Loading details...</div>
          ) : (
            <>
              {/* Assessment Completion */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <Award className="w-5 h-5" />
                  <span className="font-semibold">Assessment Completed:</span>
                  <span>{formatDate(currentUser.assessment_result.completed_at)}</span>
                </div>
              </div>

              {/* Primary & Secondary Dimensions */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-2 border-indigo-300">
                  <CardHeader>
                    <Badge className="w-fit mb-2">Primary Strength</Badge>
                    <CardTitle className="text-2xl">
                      {dimensionNames[currentUser.assessment_result.primary_dimension]}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {dimensionProfile?.tagline}
                    </CardDescription>
                  </CardHeader>
                </Card>

                <Card className="border-2">
                  <CardHeader>
                    <Badge variant="outline" className="w-fit mb-2">Secondary Strength</Badge>
                    <CardTitle className="text-2xl">
                      {dimensionNames[currentUser.assessment_result.secondary_dimension]}
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* All BUILDR Scores */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle>Complete BUILDR Score Breakdown</CardTitle>
                  <CardDescription>Scores across all 6 dimensions (out of 35 each)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(currentUser.assessment_result.scores)
                      .sort((a, b) => b[1] - a[1])
                      .map(([dimension, score]) => {
                        const percentage = (score / maxScore) * 100;
                        return (
                          <div key={dimension}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${dimensionColors[dimension]}`}>
                                  {dimensionNames[dimension]}
                                </span>
                              </div>
                              <span className="text-lg font-bold">{score}/35</span>
                            </div>
                            <Progress value={percentage} className="h-3" />
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>

              {/* Best Roles */}
              {dimensionProfile && (
                <>
                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle>Best Ministry Role Matches</CardTitle>
                      <CardDescription>Ideal positions based on {dimensionProfile.name} strength</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-3">
                        {dimensionProfile.best_roles.map((role, idx) => (
                          <div key={idx} className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                            <span className="font-semibold text-indigo-800">{role}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {dimensionProfile.great_in && (
                    <Card className="mb-8 bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Award className="h-6 w-6 text-green-600" />
                          Great In These Environments
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {dimensionProfile.great_in.map((context, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-green-600 mt-1 text-xl">★</span>
                              <span className="text-lg font-medium">{context}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Ministry Suggestions */}
              <Card>
                <CardHeader>
                  <CardTitle>Ministry Role Recommendations</CardTitle>
                  <CardDescription>Specific opportunities that match this profile</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {roleSuggestions.map((role, idx) => (
                      <div key={idx} className="p-4 border-2 rounded-lg hover:border-indigo-300 transition-colors">
                        <h4 className="font-semibold text-lg mb-1">{role.title}</h4>
                        <p className="text-gray-600">{role.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex gap-4 justify-end">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>

        {/* Edit User Modal */}
        {showEditModal && (
          <EditUserModal
            user={currentUser}
            onClose={() => setShowEditModal(false)}
            onUpdate={handleUserUpdate}
            isSuperAdmin={isSuperAdmin}
          />
        )}
      </div>
    </div>
  );
};

export default UserDetailModal;
